<script setup lang="ts">
import { computed } from 'vue'

import { Selector } from '../../'
import type { CampaignListItem, Filter } from '../../../types'
import { debounce } from '../../../utils'

const props = defineProps<{
  query: string | undefined
  shouldTruncateText?: boolean
  campaigns: CampaignListItem[] | undefined
  isLoading: boolean
  nameKey?: 'internalName' | 'name'
  label?: string
  size?: 'default' | 'large'
  isDisabled?: boolean
  width?: string | number
}>()
const emits = defineEmits<{
  (e: 'update:campaign', value: { id: number } | undefined): void
  (e: 'update:query', value: string): void
}>()
const campaign = defineModel<Filter['campaign']>('campaign')

const getCampaignName = (item?: CampaignListItem) => {
  if (!item) {
    return ''
  }
  return item[props.nameKey || 'name']
}

const campaignOptions = computed(() =>
  (props.campaigns ?? []).map((item) => {
    const campaignName = getCampaignName(item)

    return {
      label: props.nameKey === 'internalName' ? `[ID: ${item.id}] ${campaignName}` : campaignName || `(ID: ${item.id})`,
      id: item.id,
      name: campaignName,
    }
  }),
)
const campaignLabel = computed(() => {
  if (campaign.value) {
    const { id, name } = campaign.value

    if (name) {
      return name
    }

    return getCampaignName(props.campaigns?.find((item) => item.id === id))
  }

  return props.label ?? 'Campaigns'
})

const handleSearchQuery = debounce((value: string) => emits('update:query', value), 300)
const handleCampaignSelection = (item: { id: number } | null) => {
  if (!item) {
    return emits('update:campaign', undefined)
  }

  emits('update:campaign', item.id === campaign.value?.id ? undefined : item)
}

const handleVisible = () => {
  emits('update:query', '')
}
</script>
<template>
  <Selector
    :options="campaignOptions"
    :selected-option="campaign"
    :selected-label="campaignLabel"
    :show-search="true"
    :loading="isLoading"
    :placeholder="'Search campaigns'"
    :should-truncate-text="shouldTruncateText"
    show-clear-button
    :query="query"
    :label="label"
    :size="size"
    :is-disabled="isDisabled"
    :width="width ?? 350"
    @search="handleSearchQuery"
    @update:selected-option="handleCampaignSelection"
    @visible-change="handleVisible"
  />
</template>
